import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { WebShopService } from '../../services/webShop.service';
import { IDayList, IDefineTimeService, ISelectedDay, ISelectedTime, IServiceOpenTime } from './orderTimeSelector.facade';
import { CoreLogService } from '../../services/log.service';
import { InternationalisationService } from '../../forked/angular-internationalisation';
import Utils from '../../shared/utils';
import { CoreLanguageService } from '../../services/language.service';
import { BenefitRedemptionService } from '../../services/benefitRedemption.service';

@Injectable()
export class OrderTimeSelectorService implements IDefineTimeService {

  private _daySubject: BehaviorSubject<IDayList[]> = new BehaviorSubject<IDayList[]>(null);
  private _timeSubject: BehaviorSubject<IServiceOpenTime[]> = new BehaviorSubject<IServiceOpenTime[]>(null);
  private _selectedDay: ISelectedDay;
  private _selectedTime: ISelectedTime;

  constructor(
    private _webShopService: WebShopService,
    private _coreLogService: CoreLogService,
    private _coreLanguageService: CoreLanguageService,
    private _internationalisationService: InternationalisationService,
    private _benefitRedemptionService: BenefitRedemptionService
  ) { }

  public async initial() {
    const me = this;
    await me._getOpenDays();
    await me.setAvailableTimeList()
  }

  /** Get the service ID from the web shop service */
  public get serviceID() {
    const me = this;
    return me._webShopService.getServiceMode();
  }

  //TODO customer settings (portal)
  public get useOrderSubmissionTime() {
    const me = this;
    return me._webShopService.useOrderSubmissionTime;
  }

  /** Customer lead time specified in the portal */
  public get leadTime(): number {
    const me = this;
    return me._webShopService.serviceLeadTime;
  }

  /** Check if time selection is available in the order type settings */
  public get hasTimeSelection(): boolean {
    const me = this;
    return me._webShopService.ordertype.settingsModel.hasTimeSelection;
  }

  /** Check if the order is set to as soon as possible*/
  public get orderASAP(): boolean {
    const me = this;
    return me._webShopService.orderASAP;
  }

  /** The List of available days */
  public get dayList$(): Observable<IDayList[]> {
    const me = this;
    return me._daySubject.asObservable();
  }

  /** The list of service  times */
  public get timeList$(): Observable<IServiceOpenTime[]> {
    const me = this;
    return me._timeSubject.asObservable();
  }

  /** Set the selected day (drop down) */
  public setDay(day: ISelectedDay) {
    const me = this;
    if (me._selectedDay !== day) {
      me._selectedDay = day;
    }
  }

  /** Get the selected service day (drop down) */
  public get selectedDay(): ISelectedDay {
    const me = this;
    return me._selectedDay;
  }

  /** Set the Selected service time (drop down) */
  public setSelectedTime(time: ISelectedTime) {
    const me = this;
    if (me._selectedTime !== time) {
      me._selectedTime = time;
    }
  }

  /** Get the Selected service time (drop down) */
  public get selectedTime(): ISelectedTime {
    const me = this;
    return me._selectedTime;
  }

  /** set ASAP */
  public setOrderAsap(asap: boolean) {
    const me = this;
    return me._webShopService.orderASAP = asap;
  }

  /** Return German key if locale is German or English key is null/empty*/
  public getLanguageKey(keyEN: string, keyDE: string): string {
    return InternationalisationService.locale === "de" || Utils.IsNullOrEmpty(keyEN) ? keyDE : keyEN;
  }

  /**The list of open days */
  private async _getOpenDays() {
    const me = this;
    try {
      const openDays = await this._webShopService.getOpenDays();
      if (!Utils.IsNull(openDays) && openDays.length > 0) {
        openDays.forEach((day) => {
          me.getLanguageKey(day.keyEN, day.keyDE);
        });
        // Filter out days that have no valid times available
        const validOpenDays = await me._filterDaysWithValidTimes(openDays);
        if (validOpenDays.length > 0) {
          me._daySubject.next(validOpenDays);
          const firstPossibleDay: ISelectedDay = { value: validOpenDays[0].value, useTime: validOpenDays[0].use_time };
          me.setDay(firstPossibleDay);
        } else {
          //fallback No days with valid times available
          me._daySubject.next([]);
          me._coreLogService.error("OrderTimeSelectorService._getOpenDays", "No days with valid times available");
        }
      } else {
        // a day for today is automatically created
        me._createTodayFallback();
      }
    } catch (error) {
      me._coreLogService.error("OrderTimeSelectorService._getOpenDays", "get open days", error);
    }
  }

  /**
  * Filters days to only include those that have at least one valid time available.
  * A day is valid if it has times that are after the minimum valid service time (now + leadtime).
  * This prevents showing days where all pickup times have already passed.
  */
  private async _filterDaysWithValidTimes(days: IDayList[]): Promise<IDayList[]> {
    const me = this;
    const validDays: IDayList[] = [];

    for (const day of days) {
      try {
        // Get available times for this day
        const availableTimeItems = await me._webShopService.getAvailableServiceTimes(
          day.value,
          day.use_time
        );
        // If there are any valid times, keep this day
        if (availableTimeItems.length > 0) {
          validDays.push(day);
        }
      } catch (error) {
        me._coreLogService.error("OrderTimeSelectorService._filterDaysWithValidTimes", "error checking day", day, error);
        // In case of error, keep the day to avoid blocking the user
        validDays.push(day);
      }
    }
    return validDays;
  }

  public async setAvailableTimeList() {
    const me = this;
    const availableTimeItems = await me._webShopService.getAvailableServiceTimes(
      me._selectedDay.value,
      me._selectedDay.useTime
    );

    if (availableTimeItems.length === 0) {
      // No times available for the selected day - clear the time list!
      me._timeSubject.next([]);
      me._coreLogService.error("OrderTimeSelectorService.setAvailableTimeList", "No valid times available for selected day", me._selectedDay);
      return;
    }
    const firstAvailableTime = availableTimeItems[0].value;
    me.setOrderAsap(availableTimeItems[0].asap);
    me._timeSubject.next(availableTimeItems);
    me.setSelectedTime({ timeValue: firstAvailableTime });
  }

  /** Set the requirements for the order */
  public async setTimeRequirements() {
    const me = this;
    // Submit order time
    if (me.useOrderSubmissionTime) {
      me._webShopService.setServiceSubmissionTime(me._selectedTime.timeValue);
    } else {
      // Service Time : Delivery / Pickup
      me._webShopService.setServiceOrderTime(me._selectedTime.timeValue);
    }
  }

  /** Cleanup old cached data and save new time requirements */
  public async cleanupAndSaveTimeRequirements(): Promise<void> {
    const me = this;
    await me._webShopService.removeCachedServiceTime();
    await me.setTimeRequirements();
    await me._webShopService.setCachedServiceTime(me._selectedTime.timeValue);
  }

  /** Create a fallback day item for today when pre order is not available*/
  private _createTodayFallback(): void {
    const today = new Date().toISOString();
    const createTodayItem = () => {
      const todayDayItem = {
        value: today,
        key: this.getLanguageKey("Today", "Heute"),
        use_time: true,
        keyDE: this._coreLanguageService.value("phHome_Today"),
        keyEN: this._coreLanguageService.value("phHome_Today"),
        is_closed: false
      };
      this._daySubject.next([todayDayItem]);
      this.setDay({ value: today, useTime: true });
    };
    createTodayItem();
    this._internationalisationService.localeChanged.subscribe(() => {
      createTodayItem();
    });
  }

  public async getBenefitData() {
    const me = this;
    const BenefitData = await me._benefitRedemptionService.getCachedBenefitData();
    return BenefitData;
  }

  public async redeemBenefitPoints() {
    const me = this;
    const BenefitData = await me.getBenefitData();
    if (BenefitData && BenefitData.benefitProductId) {
      const state = await me._benefitRedemptionService.checkBenefitStateAndRedeemPoints(BenefitData.benefitProductId, true, -1)
      me._benefitRedemptionService.checkStateAndOpenDialogIfNeed(state);
    }
  }

  public saveWebshopBaseDataToStorage() {
    const me = this;
    me._webShopService.saveToStorage();
  }
}